// components/workshop/src/components/workshop/capacityCheckLogic.js
//
// Portert fra royken-sykkelservice sin "Bookingkapasitet i sanntid"-widget
// (src/pages/index.astro) slik at samme "første ledige tid"-beregning kan
// vises som en egen adminside i kundedata, i stedet for kun i den skjulte
// butikkmodus-panelet på kundenettsiden.
import { normalizeWorkshopSlug, isoDate as toISODateLocal } from "./workshopCapacityUtils";

function getNextMonday(baseDate, weekOffset = 0) {
  const date = new Date(baseDate);
  const day = date.getDay();
  const diff = day === 0 ? 1 : 8 - day;
  date.setDate(date.getDate() + diff + weekOffset * 7);
  return date;
}

function normalizeDateOnly(v) {
  if (!v) return "";
  return String(v).split("T")[0];
}

function getDagApiId(date) {
  const map = ["sondag", "mandag", "tirsdag", "onsdag", "torsdag", "fredag", "lordag"];
  return map[date.getDay()];
}

function isAllowedDag(date, allowedDays = []) {
  const norm = (allowedDays || []).map((d) =>
    String(d || "").toLowerCase().replace(/ø/g, "o").replace(/å/g, "a").trim()
  );
  if (!norm.length) return false;
  return norm.includes(getDagApiId(date));
}

function getWeekStartISOFromDate(dateOrIso) {
  const d = typeof dateOrIso === "string" ? new Date(dateOrIso + "T12:00:00") : new Date(dateOrIso);
  const day = d.getDay();
  const diff = day === 0 ? -6 : 1 - day;
  d.setDate(d.getDate() + diff);
  return toISODateLocal(d);
}

function getRelevantOverrides(overridesAll, verkstedSlug) {
  const wanted = normalizeWorkshopSlug(verkstedSlug);
  return (overridesAll || []).filter((o) => normalizeWorkshopSlug(o?.lokasjon || "") === wanted);
}

function effBudPerDag(cap, isoDateStr, verkstedSlug, overridesAll) {
  let dag = cap.dagsbudsjett == null || cap.dagsbudsjett === 0
    ? Math.floor((cap.ukebudsjett || 0) / 5)
    : cap.dagsbudsjett;
  const relevant = getRelevantOverrides(overridesAll, verkstedSlug);
  const dateKey = normalizeDateOnly(isoDateStr);
  const weekKey = getWeekStartISOFromDate(isoDateStr);
  const datoOv = relevant.find((x) => normalizeDateOnly(x.dato) === dateKey);
  const ukeOv = relevant.find((x) => x?.uke && getWeekStartISOFromDate(x.uke) === weekKey);
  if (datoOv?.stengt) return 0;
  if (ukeOv?.stengt) return 0;
  if (typeof datoOv?.minutter === "number") dag += datoOv.minutter;
  else if (typeof ukeOv?.minutter === "number") dag += Math.floor(ukeOv.minutter / 5);
  return Math.max(0, dag);
}

function effBudPerUke(cap, ukeStartISO, verkstedSlug, overridesAll) {
  let uke = cap.ukebudsjett || 0;
  const relevant = getRelevantOverrides(overridesAll, verkstedSlug);
  const weekKey = normalizeDateOnly(ukeStartISO);
  const ukeOv = relevant.find((x) => x?.uke && getWeekStartISOFromDate(x.uke) === weekKey);
  if (ukeOv?.stengt) return 0;
  if (typeof ukeOv?.minutter === "number") uke += ukeOv.minutter;

  const [y, m, d] = ukeStartISO.split("-").map(Number);
  let closedDays = 0;
  for (let i = 0; i < 5; i++) {
    const dayDate = new Date(y, m - 1, d + i);
    const dayISO = toISODateLocal(dayDate);
    const datoOv = relevant.find((o) => normalizeDateOnly(o.dato) === dayISO && o.stengt);
    if (datoOv) closedDays++;
  }
  if (closedDays > 0) uke = Math.floor((uke * (5 - closedDays)) / 5);
  return Math.max(0, uke);
}

// Samme prinsipp som effBudPerUke, men for et 3-dagers delvindu av uken
// (tidliguke: man–ons, senuke: ons–fre) med eget budsjett og eget
// override-felt.
function effBudPerSubUke(budsjett, ukeStartISO, verkstedSlug, overridesAll, overrideField, startOffset) {
  let val = budsjett || 0;
  const relevant = getRelevantOverrides(overridesAll, verkstedSlug);
  const weekKey = normalizeDateOnly(ukeStartISO);
  const subOv = relevant.find((x) => x?.[overrideField] && getWeekStartISOFromDate(x[overrideField]) === weekKey);
  if (subOv?.stengt) return 0;
  if (typeof subOv?.minutter === "number") val += subOv.minutter;

  const [y, m, d] = ukeStartISO.split("-").map(Number);
  let closedDays = 0;
  for (let i = 0; i < 3; i++) {
    const dayDate = new Date(y, m - 1, d + startOffset + i);
    const dayISO = toISODateLocal(dayDate);
    const datoOv = relevant.find((o) => normalizeDateOnly(o.dato) === dayISO && o.stengt);
    if (datoOv) closedDays++;
  }
  if (closedDays > 0) val = Math.floor((val * (3 - closedDays)) / 3);
  return Math.max(0, val);
}
function effBudPerTidligUke(cap, ukeStartISO, verkstedSlug, overridesAll) {
  return effBudPerSubUke(cap.tidligukebudsjett, ukeStartISO, verkstedSlug, overridesAll, "tidliguke", 0);
}
function effBudPerSenUke(cap, ukeStartISO, verkstedSlug, overridesAll) {
  return effBudPerSubUke(cap.senukebudsjett, ukeStartISO, verkstedSlug, overridesAll, "senuke", 2);
}

// Samme leveringsregel som vises i selve bookingsteget: ukebooking leveres mandag og er ferdig
// onsdag–fredag samme uke (vinnsel 28.10; før 2–5 dager, som ga lørdag), tidliguke/senuke er ferdig
// innen 0-2 dager (3-dagers vindu), dagbooking er ferdig samme dag.
export function ferdigVindu({ date, type }) {
  const pluss = (n) => { const d = new Date(date); d.setDate(d.getDate() + n); return d; };
  if (type === "uke") return { fra: pluss(2), til: pluss(4) };
  if (type === "tidliguke" || type === "senuke") return { fra: pluss(0), til: pluss(2) };
  return { fra: pluss(0), til: pluss(0) };
}

// Service må bookes før kl. 24 dagen før den starter (vinnsel, 28.10). Dagens dagservice og en
// uke/tidliguke/senuke som starter i dag tilbys ikke. Før gjaldt en frist kl. 13 samme dag.
//
// Dagservice leveres helst dagen før servicedagen (søndag regnes som lørdag), eller senest tidlig
// på servicedagen, og er ferdig samme dag: ledig dagservice i morgen = «lever i dag eller tidlig i
// morgen, ferdig i morgen». Uke og tidliguke leveres mandag, senuke onsdag.
function dagenFor(date) {
  const d = new Date(date);
  d.setDate(d.getDate() - 1);
  if (d.getDay() === 0) d.setDate(d.getDate() - 1);
  return d;
}
const startPaDagen = (d) => { const x = new Date(d); x.setHours(0, 0, 0, 0); return x; };

function scanDag(cap, verkstedSlug, overridesAll, perDagMin, { prioritert = false, minMinutter }) {
  const today = new Date();
  let firstDagDayFound = false;
  for (let i = 1; i < 60; i++) {
    const date = new Date(today);
    date.setDate(date.getDate() + i);
    if (!isAllowedDag(date, cap.dagsbudsjettdager)) continue;
    // Leveringsvindu: dagen før (er den passert, f.eks. lørdag sett fra søndag, bare tidlig samme dag).
    const dagenForDato = dagenFor(date);
    const levering = { fra: startPaDagen(dagenForDato) < startPaDagen(today) ? date : dagenForDato, til: date };

    const iso = toISODateLocal(date);
    const dagMin = effBudPerDag(cap, iso, verkstedSlug, overridesAll);
    const brukt = perDagMin[iso] || 0;
    const ledig = Math.max(0, dagMin - brukt);

    const ov = getRelevantOverrides(overridesAll, verkstedSlug);
    const wk = getWeekStartISOFromDate(iso);
    const isDayClosed = !!(
      ov.find((x) => normalizeDateOnly(x.dato) === iso)?.stengt ||
      ov.find((x) => x?.uke && getWeekStartISOFromDate(x.uke) === wk)?.stengt
    );
    const isFirstDay = !firstDagDayFound && !isDayClosed;
    if (isFirstDay) firstDagDayFound = true;
    const forceOpen = prioritert && isFirstDay;

    if (forceOpen || ledig >= minMinutter) return { days: i, date, levering, type: "dag" };
  }
  return null;
}

function scanUke(cap, verkstedSlug, overridesAll, perUkeMin, { garanti = false, minMinutter }) {
  const today = new Date();
  // "uke" har kun mandag som innleveringsdag ("leveres senest mandag"), og må bookes senest dagen
  // før: inneværende uke kan aldri tilbys (er det mandag, starter den i dag; ellers er den i gang).
  const sammeUkeMulig = false;

  for (let i = 0; i < 20; i++) {
    const brukSammeUke = i === 0 && sammeUkeMulig;
    const start = brukSammeUke ? today : getNextMonday(today, i);
    // Budsjett/overstyring er alltid nøkkel på mandagen i uken, selv når
    // intaket (start) er i dag midt i uken.
    const isoStart = brukSammeUke ? getWeekStartISOFromDate(today) : toISODateLocal(start);
    const ukeMin = effBudPerUke(cap, isoStart, verkstedSlug, overridesAll);
    const brukt = perUkeMin[isoStart] || 0;
    const ledig = Math.max(0, ukeMin - brukt);
    const forceOpen = garanti && i === 0 && ukeMin > 0;

    if (forceOpen || ledig >= minMinutter) {
      const days = Math.round((start.getTime() - today.getTime()) / 86400000);
      return { days, date: start, type: "uke" };
    }
  }
  return null;
}

// Samme prinsipp som scanUke, men for et 3-dagers budsjettvindu av uken
// (tidliguke: man–ons, senuke: ons–fre) — selve innleveringsdagen er likevel
// kun ÉN fast dag (mandag for tidliguke, onsdag for senuke), ikke en av de
// tre dagene i vinduet. Inneværende ukes innleveringsdag kan bare tilbys når
// den ikke er nådd ennå (bookes senest dagen før, se dagenFor over).
function scanSubUke(cap, verkstedSlug, overridesAll, perSubUkeMin, { garanti = false, minMinutter, type, startOffset, effBudFn }) {
  const today = new Date();
  // Mandag=0 … søndag=6, slik at det kan sammenlignes mot startOffset.
  const todayOffsetFraMandag = (today.getDay() + 6) % 7;
  // Må bookes senest dagen før innleveringsdagen: denne uka bare når den ikke er nådd ennå
  // (senuke på mandag eller tirsdag). Tidliguke (mandag) blir alltid neste uke.
  const sammeUkeMulig = todayOffsetFraMandag < startOffset;

  for (let i = 0; i < 20; i++) {
    const brukSammeUke = i === 0 && sammeUkeMulig;
    const monday = brukSammeUke ? null : getNextMonday(today, i);
    const isoMonday = brukSammeUke ? getWeekStartISOFromDate(today) : toISODateLocal(monday);
    let intake;
    if (brukSammeUke) {
      intake = new Date(isoMonday + "T12:00:00");
      intake.setDate(intake.getDate() + startOffset);
    } else {
      intake = new Date(monday);
      intake.setDate(monday.getDate() + startOffset);
    }
    const budMin = effBudFn(cap, isoMonday, verkstedSlug, overridesAll);
    const brukt = perSubUkeMin[isoMonday] || 0;
    const ledig = Math.max(0, budMin - brukt);
    const forceOpen = garanti && i === 0 && budMin > 0;

    if (forceOpen || ledig >= minMinutter) {
      const days = Math.round((intake.getTime() - today.getTime()) / 86400000);
      return { days, date: intake, type };
    }
  }
  return null;
}
function scanTidligUke(cap, verkstedSlug, overridesAll, perTidligUkeMin, opts = {}) {
  return scanSubUke(cap, verkstedSlug, overridesAll, perTidligUkeMin, { ...opts, type: "tidliguke", startOffset: 0, effBudFn: effBudPerTidligUke });
}
function scanSenUke(cap, verkstedSlug, overridesAll, perSenUkeMin, opts = {}) {
  return scanSubUke(cap, verkstedSlug, overridesAll, perSenUkeMin, { ...opts, type: "senuke", startOffset: 2, effBudFn: effBudPerSenUke });
}

export const FILTERS = [
  { key: "generell", label: "Generell service" },
  { key: "girbremse", label: "Gir-/bremseservice" },
  { key: "reklamasjon", label: "Reklamasjonshåndtering" },
  { key: "dekk", label: "Dekkskift / punktering" },
];

// Dekkskift/punktering prioriteres på første dagservice (tilbys selv om dagen er full), og kan
// også bookes på uke, tidliguke og senuke når det er ledig (vinnsel, 28.10).
// Reklamasjonshåndtering er bare ukebooking (inkl. tidliguke/senuke), med garantert første uke.
//
// Gir én oppføring per bookingtype der skanningen fant en ledig tid
// ({ type, days, date }); typer uten ledig tid er utelatt.
// Bookingtypene slik de står i Hygraph («dag», «uke», «tidliguke», «senuke», evt. med bindestrek).
export const normBookingtype = (t) => String(t || "").toLowerCase().replace(/[^a-z]/g, "");

// tillatteTyper: Set med bookingtyper tjenestene har i Hygraph (normBookingtype). En type som ikke
// er tilgjengeliggjort der (f.eks. tidliguke/senuke før de tas i bruk) vises ikke (vinnsel, 28.10).
// Uten tillatteTyper regnes alle typer ut. En butikk uten budsjett for en type får ingen treff.
export function computeStoreResults(cap, verkstedSlug, overridesAll, used, filter, minutterByFilter, tillatteTyper = null) {
  const { perDagMin = {}, perUkeMin = {}, perTidligUkeMin = {}, perSenUkeMin = {} } = used || {};
  const minMinutter = minutterByFilter[filter];
  const tillatt = (type) => !tillatteTyper || tillatteTyper.has(type);
  const resultater = [];

  if (filter !== "reklamasjon" && tillatt("dag")) {
    resultater.push(scanDag(cap, verkstedSlug, overridesAll, perDagMin, { prioritert: filter === "dekk", minMinutter }));
  }
  const garanti = filter === "reklamasjon";
  if (tillatt("tidliguke")) resultater.push(scanTidligUke(cap, verkstedSlug, overridesAll, perTidligUkeMin, { garanti, minMinutter }));
  if (tillatt("senuke")) resultater.push(scanSenUke(cap, verkstedSlug, overridesAll, perSenUkeMin, { garanti, minMinutter }));
  if (tillatt("uke")) resultater.push(scanUke(cap, verkstedSlug, overridesAll, perUkeMin, { garanti, minMinutter }));

  return resultater.filter(Boolean);
}
